import { DefaultOptionType } from 'antd/es/select'
import isArray from 'lodash/isArray'
import isEmpty from 'lodash/isEmpty'
import isNil from 'lodash/isNil'
import isNumber from 'lodash/isNumber'
import isObject from 'lodash/isObject'
import isString from 'lodash/isString'
import orderBy from 'lodash/orderBy'
import { observable } from 'mobx'

import { doesSingleOptionIsEmpty } from 'Shared/utils/array'
import { utils } from 'Shared/utils/url'
import { deleteAllSearchParams } from 'Shared/utils/url/helpers'

import { searchQueryConfig } from 'Core/constants/filters'
import {
	ConfigItem,
	FilterParams,
	FilterTypes,
	FormatOptionsConfigType,
	FormatOptionsType,
	OptionsSortType,
	OrderValues,
	SearchFilterParams,
	SortDirection,
	TableUrlParams
} from 'Core/interfaces'
import {
	entityToLabelId,
	entityToLabelValueWithId,
	entityToNameInValueLabelValue,
	entityToStringLabelValue,
	valueToLabelValue,
	valueToStringLabelValue
} from 'Core/utils/dataFormat'
import { sortOptions } from 'Core/utils/sorting'

import { SupplierInvoice } from 'Portals/Internal/entities'

import { GenericParamsI, LocationSearch, StateProvince } from './types'

/**
 * Prepares generic parameters for table URL by extracting and formatting pagination, search, sorting, and filter data.
 * @param {GenericParamsI} params - Object containing pageNumber, pageSize, searchQuery, sortingParams, searchFilters, filters
 * @param {number} params.pageNumber - Current page number
 * @param {number} params.pageSize - Number of items per page
 * @param {string} params.searchQuery - Search query string
 * @param {Object} params.sortingParams - Sorting configuration with field and order
 * @param {SearchFilterParams[]} params.searchFilters - Array of search filter objects
 * @param {Object} params.filters - Object containing filter configurations
 * @param {boolean} [fixQueryParam=false] - Whether to use 'searchQuery' instead of 'query' parameter
 * @returns {TableUrlParams} Formatted parameters object for URL
 */
export const prepareGenericParams = (
	{ pageNumber, pageSize, searchQuery, sortingParams, searchFilters, filters }: GenericParamsI,
	fixQueryParam?: boolean
): TableUrlParams => {
	const params = {} as TableUrlParams
	params.page = pageNumber

	if (pageNumber) {
		params.page = pageNumber
	}

	if (pageSize) {
		params.pageSize = pageSize
	}

	// TODO: make consistent on backend query and searchQuery
	if (searchQuery && !fixQueryParam) {
		params.query = searchQuery
	} else if (searchQuery && fixQueryParam) {
		params.searchQuery = searchQuery
	}

	if (sortingParams) {
		params.sortField = sortingParams.field
		params.sortOrder = sortingParams.order
	}

	if (searchFilters) {
		searchFilters.forEach((filter) => {
			const { apiSearchFieldName, data } = filter
			if (apiSearchFieldName === searchQueryConfig.searchQuery.apiSearchFieldName && data) {
				// TODO: make consistent on backend query and searchQuery
				if (fixQueryParam) {
					params.searchQuery = data
				} else {
					params.query = data as string
				}
			} else {
				params[filter.apiSearchFieldName] = filter.data
			}
		})
	}

	if (isObject(filters)) {
		Object.values(filters).forEach((filter) => {
			const { data, apiFilterFieldName, filterType, emptyValue } = filter

			if (data) {
				const isDataShouldBeFiltered =
					isString(emptyValue) &&
					(isArray(data) || isString(data)) &&
					data.includes(emptyValue)

				if (isDataShouldBeFiltered) {
					return
				}

				if (isArray(data) && filterType !== FilterTypes.MULTI_VALUE) {
					params[apiFilterFieldName] = data[0]
					return
				}

				if (
					filterType === FilterTypes.MULTI_VALUE &&
					isArray(data) &&
					(doesSingleOptionIsEmpty(data) || isEmpty(data))
				) {
					return
				}

				params[apiFilterFieldName] = data
			}
		})
	}
	return params
}

/**
 * Sets URL parameters based on search filters and filter objects.
 * @param {Object} config - Configuration object
 * @param {SearchFilterParams[]} config.searchFilters - Array of search filter objects with URL param mappings
 * @param {Object} config.filters - Object containing filter configurations with URL parameters
 * @param {boolean} [config.isDeleteAllSearchParams=true] - Whether to delete all existing search parameters first
 */
export const setURLParams = ({
	searchFilters,
	filters,
	isDeleteAllSearchParams = true
}: {
	searchFilters: SearchFilterParams[]
	filters: { [key: string]: FilterParams }
	isDeleteAllSearchParams?: boolean
}) => {
	isDeleteAllSearchParams && deleteAllSearchParams()
	if (searchFilters.length > 0) {
		searchFilters.forEach((filter) => {
			const { data, UrlParam, UrlParamLabel, label, suffixData } = filter
			if (data) {
				utils.updateUrlParams(UrlParam, data)
				if (UrlParamLabel && label) {
					utils.updateUrlParams(UrlParamLabel, label)
				}

				if (suffixData?.label) {
					utils.updateUrlParams(suffixData.urlParam, suffixData.label)
				}
			}
		})
	}

	if (isObject(filters)) {
		Object.values(filters).forEach((filter) => {
			const { data, UrlParam } = filter
			if (isArray(data)) {
				data.forEach((value) => {
					if (isEmpty(value)) {
						return
					}
					utils.appendSearchParam(UrlParam, value)
				})
				return
			}

			if (data) {
				utils.updateUrlParams(UrlParam, data)
			}
		})
	}
}

/**
 * Gets a default store value object with null value and loading state.
 * @template T - The type of the store value
 * @returns {StoreValue<T>} Default store value object with value=null and isLoading=false
 */
export const getDefaultStoreValue = <T>(deep: boolean = false): StoreValue<T> =>
	deep
		? observable({
			value: null as T | null,
			isLoading: false
		  })
		: {
			value: null,
			isLoading: false
		  }

/**
 * Gets a default store array value object with empty array and loading state.
 * @template T - The type of array elements (defaults to Entity)
 * @param {boolean} [deep=false] - Whether to make the store array observable for deep reactivity
 * @returns {StoreArrayValue<T>} Store array value object with empty array and isLoading=false
 */
export const getDefaultStoreArrayValue = <T = Entity>(deep: boolean = false): StoreArrayValue<T> => 
	deep
		? observable({
			value: [] as T[],
			isLoading: false
		  })
		: {
			value: [],
			isLoading: false
		  }

/**
 * Gets a default store boolean value object with the specified boolean value and loading state.
 * @param {boolean} [value=false] - The initial boolean value
 * @returns {StoreBooleanValue} Store boolean value object with specified value and isLoading=false
 */
export const getDefaultStoreBooleanValue = (value: boolean = false): StoreBooleanValue => ({
	value,
	isLoading: false
})

/**
 * Checks if any entity in the array is currently loading.
 * @param {Object[]} entities - Array of entities with loading state
 * @param {boolean} entities[].isLoading - Loading state of each entity
 * @returns {boolean} True if any entity is loading, false otherwise
 */
export const isLoadingEntities = (entities: { isLoading: boolean }[]): boolean =>
	entities.some(({ isLoading }) => isLoading)

/**
 * Formats search string options for a specific field in configuration.
 * @template F - Field name type
 * @template Config - Configuration object type
 * @param {F} fieldName - The field name in the configuration
 * @returns {Function} Function that accepts options and config and returns formatted DefaultOptionType array
 */
export const formatSearchStringOptions =
	<F extends keyof Config, Config>(fieldName: F) =>
	(options: (string | Entity)[], config: Config): DefaultOptionType[] => {
		if (isEmpty(options)) {
			return []
		}

		const configItem = config[fieldName] as ConfigItem<F>

		return [createFormatOption(configItem, options)]
	}

/**
 * Formats search options specifically for supplier invoice configuration.
 * @template F - Fields object type
 * @template Config - Configuration type
 * @template FieldName - Field name type
 * @template OptionResponse - Option response type
 * @param {Entity[]} options1 - Array of entity options (supplier invoice numbers)
 * @param {SupplierInvoice.InvoiceHistory.Config} searchSupplierInvoiceConfig - Configuration for supplier invoice search
 * @param {F} fields - Field names to process
 * @returns {Function} Function that processes options and returns formatted option groups
 */
export const formatSearchOptionsForSupplierInvoice =
	<F extends {}, Config, FieldName extends keyof F, OptionResponse>(
		options1: Entity[],
		searchSupplierInvoiceConfig: SupplierInvoice.InvoiceHistory.Config,
		fields: F
	) =>
	(options: OptionResponse, searchSupplierInvoiceConfig: Config) => {
		if (isEmpty(options)) {
			return []
		}

		const supplierInvoiceNumberOptions = options1

		const configType = searchSupplierInvoiceConfig as FormatOptionsConfigType<F>

		return Object.values(fields)
			.filter((fieldName) => configType[fieldName as FieldName])
			.map((fieldName) => {
				const configItem = configType[fieldName as FieldName]
				return createFormatOption(configItem, supplierInvoiceNumberOptions)
			})
	}

/**
 * Formats search options by processing field-specific configurations.
 * @template F - Fields object type
 * @template Config - Configuration type
 * @template FieldName - Field name type
 * @template OptionResponse - Option response type
 * @param {F} fields - Field names to process from the configuration
 * @returns {Function} Function that processes options and config, returns formatted option groups
 */
export const formatSearchOptions =
	<F extends {}, Config, FieldName extends keyof F, OptionResponse>(fields: F) =>
	(options: OptionResponse, config: Config) => {
		if (isEmpty(options)) {
			return []
		}

		const configType = config as FormatOptionsConfigType<F>
		const optionsType = options as FormatOptionsType<F>

		return Object.values(fields)
			.filter((fieldName) => configType[fieldName as FieldName])
			.map((fieldName) => {
				const configItem = configType[fieldName as FieldName]
				const data = optionsType[fieldName as FieldName]
				return createFormatOption(configItem, data)
			})
	}

/**
 * Creates formatted option groups from configuration and data.
 * @template SearchResultFields - Type of search result fields
 * @param {ConfigItem<SearchResultFields>} configItem - Configuration item with formatting rules
 * @param {(Key|Entity)[]} data - Array of data items to format
 * @returns {Object} Option group with label, key, and sorted options
 */
export const createFormatOption = <SearchResultFields>(
	configItem: ConfigItem<SearchResultFields>,
	data: (Key | Entity)[]
) => {
	const {
		apiFieldName,
		filterType,
		apiSearchFieldName,
		UrlParam,
		UrlParamLabel,
		customOptionFormatter,
		optionsSortType,
		suffixData
	} = configItem

	const options = data.map((item, index) => {
		let option: LabelValue<Key>
		if (customOptionFormatter) {
			option = customOptionFormatter(item)
		} else {
			option = getOptionByCondition<SearchResultFields>(item, configItem)
		}

		return {
			...option,
			key: `${configItem.label}-${option.value}-${index}`,
			value: `${apiFieldName}-${option.value}`,
			props: {
				filterType,
				apiSearchFieldName,
				value: option.value,
				label: option.label,
				UrlParam,
				UrlParamLabel,
				suffixData
			}
		}
	})

	const sortedOptions = sortOptions({
		options,
		sortType: optionsSortType || OptionsSortType.default
	})

	return {
		label: configItem.label,
		key: configItem.label,
		options: sortedOptions
	}
}

/**
 * Gets a formatted label-value pair based on condition flags in the configuration.
 * @template T - Configuration type
 * @param {Key|Entity} item - The item to format (string, number, or entity)
 * @param {ConfigItem<T>} config - Configuration object with formatting condition flags
 * @param {boolean} config.isLabelId - Format as label-id pair
 * @param {boolean} config.isLabelCode - Format as code entity
 * @param {boolean} config.isLabelState - Format as state/province
 * @param {boolean} config.isTurkishUser - User is Turkish (affects state formatting)
 * @param {boolean} config.isLabelAndId - Format as label with ID
 * @param {boolean} config.isLabelInValue - Format with label in value
 * @param {boolean} config.isLocationIdName - Format as location ID and name
 * @returns {LabelValue<Key>} Formatted label-value object
 */
export const getOptionByCondition = <T>(
	item: Key | Entity,
	{
		isLabelId,
		isLabelCode,
		isLabelState,
		isTurkishUser,
		isLabelAndId,
		isLabelInValue,
		isLocationIdName
	}: ConfigItem<T>
): LabelValue<Key> => {
	if (isString(item)) {
		return valueToLabelValue(item)
	} else if (isNumber(item)) {
		return valueToStringLabelValue(item)
	} else if (isLabelId) {
		return entityToLabelId(item)
	} else if (isLabelCode) {
		return codeEntityToLabelValue(item as CodeEntity)
	} else if (isLabelState) {
		return formatStateProvinceToLabelValue(item as StateProvince, isTurkishUser)
	} else if (isLabelAndId) {
		return entityToLabelValueWithId(item)
	} else if (isLocationIdName) {
		return formatLocationName(item as LocationSearch)
	} else if (isLabelInValue) {
		return entityToNameInValueLabelValue(item)
	} else {
		return entityToStringLabelValue(item)
	}
}

/**
 * Formats a state/province entity to a label-value pair with extra metadata.
 * @param {StateProvince} entity - State or province entity to format
 * @param {boolean} [isTurkishUser=false] - Whether the user is Turkish (affects formatting)
 * @returns {LabelValueExtra} Formatted object with label, value, and country code extra
 */
export const formatStateProvinceToLabelValue = (
	entity: StateProvince,
	isTurkishUser: boolean = false
): LabelValueExtra => {
	let label = entity.name

	if (!isTurkishUser && entity.countryCode !== 'TUR') {
		if (entity.code) {
			label += `, ${entity.code}`
		}

		if (entity.countryCode) {
			label += ` (${entity.countryCode})`
		}
	} else {
		if (entity.region) {
			label += ` (${entity.region})`
		}
	}

	return {
		label,
		value: entity.id.toString(),
		extra: entity.countryCode ?? ''
	}
}

/**
 * Converts a code entity to a label-value pair.
 * @param {Pick<CodeEntity, 'code'|'id'>} item - Entity with code and id properties
 * @returns {LabelValue<number>} Label-value pair with code as label and id as value
 */
export const codeEntityToLabelValue = (
	item: Pick<CodeEntity, 'code' | 'id'>
): LabelValue<number> => ({
	label: item.code,
	value: item.id
})

/**
 * Formats a location entity to a label-value pair.
 * @param {LocationSearch} entity - Location entity with company location ID, name, and id
 * @returns {LabelValue<number>} Label-value pair combining company location ID and name
 */
export const formatLocationName = (entity: LocationSearch): LabelValue<number> => ({
	label: `${entity.companyLocationId} - ${entity.name}`,
	value: entity.id
})

/**
 * Sorts items by name in the specified direction.
 * @template T - Item type with a name property
 * @param {SortDirection|undefined} order - Sort direction (ASC, DESC, or undefined)
 * @param {T[]} items - Array of items to sort
 * @returns {T[]} Sorted items or original items if no sort order specified
 */
export function getResortedItemsByName<T extends { name: string }>(
	order: SortDirection | undefined,
	items: T[]
) {
	if (!order) {
		return items
	}
	const correctOrder = order === SortDirection.ASC ? OrderValues.asc : OrderValues.desc
	return orderBy(items, 'name', correctOrder)
}

/**
 * Extracts a single filter value from an array or returns the value directly.
 * @template T - Type of the value
 * @param {T[]|T|undefined|null} data - Array, single value, or null/undefined
 * @returns {T|null} First element if array, the value itself, or null
 */
export const getFilterValue = <T>(data: T[] | T | undefined | null) => {
	if (isNil(data)) {
		return null
	}

	return Array.isArray(data) ? data[0] : data
}

/**
 * Formats a state/province entity to a readable string representation.
 * @param {StateProvince} state - State or province entity to format
 * @returns {string} Formatted string with name, code, and country code
 */
export const formatStateProvince = (state: StateProvince): string => {
	let label = state.name

	if (state.code) {
		label += `, ${state.code}`
	}

	if (state.countryCode) {
		label += ` (${state.countryCode})`
	}

	return label
}

/**
 * Formats a state/province entity to a label-value pair for use in options.
 * @param {StateProvince} state - State or province entity to format
 * @returns {LabelValue} Label-value pair with formatted state string
 */
export const formatStateProvinceOption = (state: StateProvince): LabelValue => ({
	label: formatStateProvince(state),
	value: state.id.toString()
})
